import React, { useRef } from "react";
import { Link } from "react-router-dom";

import { Calculator, ArrowUpRight } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import BannerCTA from "../CTA/CTABanner.jsx";
import Tooltip from "./Tooltip";
import Seo from "../Seo.jsx";
import guidesConfig from "../../config/guidesConfig";
import ScrollModal from "../ui/ScrollModal.jsx";
import "./guides.css";
import cheTotBack from "./images/che-tot-back.png";
import cheTotLay from "./images/che-tot-lay.png";
import om from "./images/om.png";

const FreeBetsGuide = () => {
  const scrollRef = useRef(null);
  const meta = guidesConfig.freeBetsGuide?.seo || {};


  return (
    <div className="guides-content">
            <Seo title={meta.title} description={meta.description} url={guidesConfig.freeBetsGuide?.route || "/guides"}/>

      <p>
        Welcome to your one-stop resource for turning free bet offers into real profit.
        Whether you’re a complete beginner or have dabbled in matched betting before, this guide will walk you through the process of profiting from free bets. We’ll break down the basics, explain the step-by-step process, and provide tips and tools to help you maximise your returns.
      </p>
      
      <hr />
      
      <h2>What Are Free Bet Offers?</h2>
<p>
  Free bet offers are promotional offers designed to attract new customers or reward loyal ones.
</p>
<p>
  These offers provide you a chance to lock in profit without using your own money. When you use a free bet, you get to keep any winnings generated, but the original stake is usually not returned – a concept known as <strong className="key">SNR</strong> (Stake Not Returned). In simple terms, you only keep the profit from your free bet.
</p>
<p>
  There are several types of free bet offers, including:
</p>
<ul>
  <li>
    <strong>Bet and Get Offers:</strong> The most common offer – place a qualifying bet, then you receive a free bet bonus. 
  </li>
  <li>
    <strong>Risk-Free Bets:</strong> If your qualifying bet loses, the bookmaker refunds your stake as a free bet.
  </li>
  <li>
    <strong>Reload Offers:</strong> Ongoing promotions for existing customers that provide free bets or bonuses on subsequent bets or deposits.
  </li>
</ul>
<p>
This guide focuses primarily on <em>bet and get</em> offers.
</p>
<h6 className="warn">Set your odds format to decimal at the bookie before you start. We have a <a href="fractional-to-decimal">handy odds converter</a> if you have any problems.</h6>

      
      <hr />
      
      <h2>How Do Free Bets Work?</h2>
      <p>
        The idea is simple: by strategically placing bets both for and against outcomes, you can lock in a profit regardless of the outcome. Here’s how:
      </p>
      <p>Imagine we are targeting a  Bet £10, Get £30 in free bets offer</p>
      <h3>Step 1: Place a Qualifying Bet</h3>
      <p>
        Place a real-money <span className="back">back</span> bet <em>for</em> an outcome at the bookie.
      </p>
      <h6 className="info">A <span className="back">back</span> bet is a bet that an outcome <em>will</em> happen.</h6>
      <h3>Step 2: Cancel Out the Qualifying Bet</h3>

      <p>
        Place a real-money <span className="lay">lay</span> bet <em>against</em> the same outcome at a betting exchange.
      </p>
      <h6 className="info">A <span className="lay">lay</span> bet is a bet that an outcome <em>will not</em> happen.</h6>
      <p>
        The result is a small “qualifying loss” of about 10p to £1, which unlocks your free bet.
      </p>

      <h3>Step 3: Use Your Free Bet</h3>
      <p>
        Once your free bet is in your account, repeat the process. This time, your{" "}
        <span className="back">back</span> bet is placed using the free bet, and you <span className="lay">lay</span> it again on the exchange.
      </p>
      <p>
        Because the stake isn’t yours this time, you <strong>keep most of the winnings</strong> as profit — typically around{" "}
        <strong>70–80% of the free bet value</strong>. For example, a £30 free bet can turn into{" "}
        <strong>£21–£24 profit</strong>.
      </p>
      
     
      <div className="example-container">
        <h4>Example: Turn £10 into £20+ Profit</h4>
        <p>
          Let’s say <Tooltip content="Example bookmaker">Paddy Power</Tooltip> offers “Bet £10, Get £40 in free bets”.
        </p>
        <ol>
          <li>
            You place a £10 <span className="back">back</span> bet for Team A to win at Paddy Power.
          </li>
          <li>
            You <span className="lay">lay</span> Team A to win on{" "}
            <Tooltip content="A popular betting exchange">Smarkets</Tooltip>.
          </li>
          <li>This costs you about 10p-£1 as a qualifying loss.</li>
          
          <li>You receive your £40 in free bets (usually split into 4 x £10).</li>
          <li>
            For each free bet:
            <ul>
              <li>You back a <Tooltip content="Your stake isn't usually returned if you win a free bet, so high odds are important.">high-odds selection</Tooltip> (e.g. odds of 6.0)</li>
              <li>You lay the same selection at the exchange</li>
              <li>You lock in around £7–£8 profit per free bet</li>
            </ul>
          </li>
        </ol>
        <p>
          After using all four, you’re left with around <strong>£28–£32 profit</strong> — and that’s just from one offer.
        </p>
      </div>
      <h6 className="warn">
      Until your bets settle, your potential exchange loss will be "tied up", so that the exchange can guarantee you'll pay your backer if they win. This sum is called <Tooltip className="tooltip" content=" The amount you stand to lose if the bet doesn’t go your way.">
            liability
          </Tooltip>.
        </h6>
      <h6 className="info">
        Tip: Use a matched betting calculator to determine the ideal lay stake and ensure your qualifying loss is minimal.

      </h6>
      
      <CTALarge 
        to="/calculators/lay-stake-calculator" 
        text="Go to the Lay Stake Calculator" 
        icon={Calculator} 
      />
      
      <section>
  <h2>How to Find Matches</h2>
  <h3>Option 1: Find Matches Manually</h3>
  
  <ol>
    <li>
      <strong>Find Qualifying Events:</strong>
      <p>We're looking for a football match to bet on to qualify for a £10 free bet at <Tooltip content="A popular bookmaker">Paddy Power</Tooltip>.</p>
      <p>
        Look for an event where you can place your <span className="back">back</span> bet, that qualifies for the offer. You find an Arsenal v Chelsea match.
      </p>
      <img src={cheTotBack} alt="Paddy Power Chelsea v Tottenham football odds" />
    </li>
    <li>
      <strong>Manually Compare Odds:</strong>
      <p>
        You find the same event at <Tooltip content="A popular betting exchange">Smarkets.</Tooltip>.
      </p>
      <img src={cheTotLay} alt="Smarkets Chelsea v Tottenham football lay odds" />
    </li>
  </ol>
      <h6 className="warn">The <span className="lay">lay</span> odds are on the right in blue on Smarkets.</h6>
      <p>
        You use the <Link to="/lay-stake-calculator">free bet calculator</Link> to assess how close the match is. You'd retain 93.6% of your stake by <span className="back">backing</span> and <span className="lay">laying</span> Chelsea, so it's an acceptable match. Your qualifying loss is 64p.
      </p>
      
  <h3>Option 2: Get a Tool That Finds Matches For You</h3>
  <p>
    Manual matching is tedious and time-consuming. Most matched bettors use a tool to simplify the process.
  </p>
  <p>
    Oddsmonkey is the most popular tool in the UK, and it helps matched bettors find efficient matches at scale and place bets with a click.
  </p>
  <img src={om} alt="Oddsmonkey Matched Betting Tool" />
</section>
   
      <hr />
      
      <h2>Some Tips For Free Bet Offers</h2>
      <ul>
        <li>
          <strong>Always Check Offer Details:</strong> Read the terms and conditions carefully to avoid any surprises.
        </li>
        <li>
          <strong>Use Calculators:</strong> They're the only way to quickly calculate the right <span className="lay">lay</span> bets and reduce your risk of errors.
        </li>
        <ScrollModal scrollContainerRef={scrollRef} props={{ctaKey: "om2"}} />

        <li>
          <strong>Stay Organised:</strong> Keep a detailed record of every bet, promotion, and profit.
        </li>
        <li>
          <strong>Manage Your Bankroll:</strong> Only use funds you can afford to set aside. Make sure you have enough for covering <Tooltip className="tooltip" content=" The amount you stand to lose if the bet doesn’t go your way.">
            liabilities
          </Tooltip> on lay bets.
        </li>
      </ul>
      
      <hr />
      
      <h2>Final Thoughts</h2>
      <p>
        Profiting from free bets is a fantastic way to generate extra income with almost no risk. With a clear understanding of the process, the right tools, and a methodical approach, you can transform bookmaker promotions into steady profits.
      </p>
      
      <BannerCTA 
        to="/calculators/lay-stake-calculator" 
        text="Try the Lay Stake Calculator" 
        icon={ArrowUpRight} 
      />
    </div>
  );
};

export default FreeBetsGuide;
